/**
 * @file
 * Content shown in a single AoT tab-switcher
 */

import * as React from 'react';
import DOMPurify from 'dompurify';

// Components
import Loader from '../../components/Loader/Loader';
import Message from '../../components/Message/Message';

// Config
import {
  CMS_AOY_STANDINGS_ROUTE,
  DEFAULT_STRING_NO_TABLE_RESULTS,
  getPhotoById,
  getProfileUrlById,
} from '../../config';

// Utils
import * as api from '../../util/api';
import pointsStandingsParams from '../../util/pointsStandingsParams';

// Types
import {
  AoyRanks,
  AoyTournamentType,
  IAoyAngler,
  IAoyFirstPlaceAngler,
} from './types';

declare global {
  interface Window { bassConfig: any; }
}

interface IProps {
  // tournament type e.g. 91, 531, 551, etc.
  type: AoyTournamentType;
  // Year
  year?: number;
  // Temp solution to hide meta in AoY tabs
  showMeta?: boolean;
}

interface IState {
  data: IAoyAngler[];
  firstPlaceAngler: MaybeFirstPlaceAngler;
  kayakContent: string;
  kayakImgUrl: string;
  loading: boolean;
}

type MaybeFirstPlaceAngler = IAoyFirstPlaceAngler | null;

class AoyTab extends React.PureComponent<IProps, IState> {
  public state: IState = {
    data: [],
    firstPlaceAngler: null as MaybeFirstPlaceAngler,
    kayakContent: '',
    kayakImgUrl: '',
    loading: true,
  };

  /**
   * Build up api uri specific to AoY
   */
  public apiUri = () => {
    const { type, year } = this.props;
    const query = pointsStandingsParams(type, year);
    if (type === AoyTournamentType.ROY) {
      return `/wp-json/data/v1/points-standings-roy?${query}`;
    }
    return `/wp-json/data/v1/points-standings?${query}`;
  };

  /**
   * Fetch data
   */
  public fetchData = async () =>
    api.get({
      callback: this.receiveData(),
      uri: this.apiUri(),
    });

  /**
   * Set data
   */
  public receiveData = () => (data: IAoyAngler[]) =>
    this.setState({
      data: data || [],
      firstPlaceAngler: this.findFirstPlaceAngler(data),
      loading: false,
    }
  );

  /**
   * Finds first place angler from data set.
   * Returns null if no first place angler is found.
   */
  public findFirstPlaceAngler = (data: IAoyAngler[]): MaybeFirstPlaceAngler => {
    const angler =
      data && data.find(({ rank }) => rank === AoyRanks.FIRST_PLACE);
    return angler
      ? {
          city: angler.city,
          state: angler.state,
          name: angler.name,
          score: angler.score,
          tms: parseInt(angler.tms ?? angler.fishermenid, 10),
        }
      : null;
  };

  public async fetchKayakData() {
    try {
      // Grab kayak data page id from global bassConfig (defined in class-bass-blocks.php).
      const { kayakDataId } = window.bassConfig.bass_aoy;
      const res = await fetch(`/wp-json/wp/v2/pages/${kayakDataId}`);
      if (!res.ok) {
        throw new Error(res.statusText);
      }

      // Parse data and assign it to local state if there are results.
      const data = await res.json();
      this.setState({kayakContent: data.content.rendered});

      // Get kayak sponsorship featured image.
      const imgId = data?.featured_media;
      if (imgId) {
        const imgRes = await fetch(`/wp-json/wp/v2/media/${imgId}`);
        const imgData = await imgRes.json();
        this.setState({kayakImgUrl: imgData?.media_details.sizes.medium.source_url});
      }
    } catch(e) {
      console.error(e);
      this.setState({kayakContent: `<p>${DEFAULT_STRING_NO_TABLE_RESULTS}</p>`})
    } finally {
      this.setState({loading: false});
    }
  }

  public componentDidUpdate = async (prevProps: IProps) => {
    const { type: prevType } = prevProps;
    const { type: currentType } = this.props;

    // Fetch new data if type (91, 531, 551, etc) changes
    if (!(prevType === currentType)) {
      // Quick loading state
      this.setState({
        loading: true,
      });

      // If kayak tab selected and kayakContent not already set, get kayak data.
      // Else fetch data as normal.
      if (1 === currentType && !this.state.kayakContent) {
        await this.fetchKayakData()
      } else {
        await this.fetchData();
      }
    }
  };

  public async componentDidMount() {
    await this.fetchData();
  }

  public render() {
    const { firstPlaceAngler: angler, data, loading } = this.state;
    const { type } = this.props;

    // Return Kayak Tab if active.
    if (1 === type) {
      return (
        <div className="kayak-widget-container">
          {!loading ? (
            <>
             <div
               className="table-wrapper"
               dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(this.state.kayakContent) }}
             />
             {this.state.kayakImgUrl ? (
              <div className="img-wrapper">
                <img
                  alt="sponsorship badge"
                  className="sponsor-badge"
                  src={this.state.kayakImgUrl}
                />
              </div>
             ) : null}
             </>
          ) : <Loader loading={loading} />}
        </div>
      );
    }
    return (
      <div className="aoy__widget" data-testid="aoy-results-homepage">
        <Loader loading={loading} />

        <Message
          message="No Angler of the Year results at this time."
          customClass="aoy-tabs__no-results-message"
          shouldShow={!loading && !data.length}
        />

        {!loading && data.length ? (
          <React.Fragment>
            {/* LEFT SIDE */}
            {angler && (
              <div
                className="aoy__widget__feature"
                data-testid="aoy-results-featured-angler"
              >
                <div
                  className="aoy__widget__feature__image"
                  data-testid="aoy-results-featured-angler"
                >
                  <Loader />
                  <img src={getPhotoById(angler.tms)} alt={angler.name} />
                </div>

                <span className="aoy__widget__feature__score">
                  <div className="aoy__widget__feature__score__head">
                    1st Place
                  </div>
                  <div className="aoy__widget__feature__score__body">
                    {angler.score}
                  </div>
                  <div className="aoy__widget__feature__score__footer">
                    POINTS
                  </div>
                </span>

                <div
                  className="aoy__widget__feature__background"
                  data-testid="aoy-results-featured-angler"
                >
                  <div
                    className="aoy__widget__feature__caption"
                    data-testid="aoy-results-featured-angler"
                  >
                    <span className="aoy__widget__feature__name">
                      {angler.name}
                    </span>

                    <div className="aoy__widget__feature__city">
                      {angler.city}, {angler.state}
                    </div>

                    <div className="aoy__widget__feature__about">
                      <a href={getProfileUrlById(angler.tms)}>
                        More About {angler.name.split(' ')[0]}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="aoy__widget__moreLink">
                  <a href={`${CMS_AOY_STANDINGS_ROUTE}/${type}`}>
                    View Full Standings
                  </a>
                </div>
              </div>
            )}
          </React.Fragment>
        ) : null}
      </div>
    );
  }
}

export default AoyTab;
